import { Layout } from 'components/layout';
import { publicRoute } from 'components/with-auth/public-route';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import { prefetchPublicListingsState } from 'lib/api/prefetch-public-listings';
import { getSiteOrigin } from 'lib/api/public-server-fetch';
import { GetServerSideProps, NextPage } from 'next';
import { getSession } from 'next-auth/react';
import Head from 'next/head';
import React from 'react';
import { PATHS } from 'settings/config';
import { MarketplaceNotificationDialog } from 'components/marketplace-notification-dialog';
import { MarketplaceListings } from '../../components/marketplace';

interface ListingPageProps {
  img: string;
  canonicalUrl: string;
  /** ISO date of the most-recently-updated public listing (freshness signal). */
  lastModified?: string;
}

// schema.org CollectionPage so crawlers / AI engines get a content-freshness
// signal for the marketplace (and homepage, which renders this same component).
// `dateModified` is a real date derived from the listings, omitted when none is
// available rather than fabricated. `<` is escaped to avoid breaking the script.
const buildCollectionStructuredData = (
  canonicalUrl: string,
  lastModified?: string
): string => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Agora Livestock Marketplace',
    description:
      'Browse cattle, sheep and goat listings for sale and wanted across Australia.',
    url: canonicalUrl,
    ...(lastModified ? { dateModified: lastModified } : {}),
  };
  return JSON.stringify(data).replace(/</g, '\\u003c');
};

export const ListingsPage: NextPage<ListingPageProps> = ({
  img,
  canonicalUrl,
  lastModified,
}) => {
  const [
    notificationDialogOpen,
    openNotificationDialog,
    closeNotificationDialog,
  ] = useDialogToggle(false);

  return (
    <Layout>
      <Head>
        <title>Agora Marketplace | Buy &amp; Sell Livestock</title>
        <meta
          name='description'
          content='Browse cattle, sheep and goat listings on the Agora Livestock marketplace. Find livestock for sale and wanted-to-buy listings across Australia.'
        />
        <link rel='canonical' href={canonicalUrl} />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: buildCollectionStructuredData(canonicalUrl, lastModified),
          }}
        />
        <meta name='og:image' content={img} />
        <meta property='og:image' content={img} />
        <meta property='og:type' content='website' />
        <meta property='og:url' content={canonicalUrl} />
        <meta property='og:title' content={'Agora Livestock - Marketplace'} />
        <meta
          property='og:description'
          content='Browse cattle, sheep and goat listings on the Agora Livestock marketplace.'
        />
        <meta name='twitter:title' content='Agora Livestock - Marketplace' />
        <meta name='twitter:image' content={img} />
        <meta name='twitter:card' content='summary_large_image' />
        <meta property='twitter:domain' content='agoralivestock.com.au' />
      </Head>
      <MarketplaceListings
        route={PATHS.marketplace.listings}
        openNotificationDialog={openNotificationDialog}
      />
      <MarketplaceNotificationDialog
        isOpen={notificationDialogOpen}
        onDismiss={() => {
          closeNotificationDialog();
        }}
      />
    </Layout>
  );
};

export default publicRoute(ListingsPage, {
  marketplace: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req, res } = context;
  const img = '/images/marketplace-social.jpg';
  const canonicalUrl = `${getSiteOrigin(req)}${PATHS.marketplace.listings}`;

  // Client-side navigations fetch props via /_next/data/... — skip the session
  // lookup and listings prefetch; the browser QueryClient / SessionProvider
  // already hold everything. Full-page loads (crawlers, direct visits) still
  // run the full branch below.
  if (req.url?.startsWith('/_next/data')) {
    return { props: { img, canonicalUrl } };
  }

  const session = await getSession(context);

  let dehydratedState: unknown = null;
  // Freshest listing date, used as the CollectionPage `dateModified` signal.
  let lastModified: string | undefined;

  // Only prefetch for anonymous visitors (crawlers). Authenticated users get a
  // richer, token-scoped result client-side, so seeding the anon cache for them
  // would be wasted work.
  if (!session) {
    // Cache the anonymous (public) HTML at the CDN so crawler traffic doesn't
    // hit the origin every request. Authenticated responses are never cached.
    res.setHeader(
      'Cache-Control',
      'public, s-maxage=300, stale-while-revalidate=600'
    );

    ({ dehydratedState, lastModified } = await prefetchPublicListingsState(
      query
    ));
  }

  return {
    props: {
      img,
      canonicalUrl,
      session,
      ...(lastModified ? { lastModified } : {}),
      ...(dehydratedState ? { dehydratedState } : {}),
    },
  };
};
